import React, { useState, useContext, useRef, useEffect } from 'react';
import { updateSweepDetails, getVaultApiBearerToken } from '../../api/agency-profile';
import { UserContext } from '../../context/UserContextProvider';
import { ClientConfigurationContext } from "../../context/ClientConfigurationContextProvider";
import { logDetails, logError } from "../../api/logger-configuration";
import { useNavigate } from 'react-router-dom';
import { publishSweepEvent, getRequestId } from './PublishSweepEventRequestHelper';

export default function EnrollForm({ agencyProfile, branchProfile, isBranch, onCancel, setErrorAlert }) {
    const user = useContext(UserContext);
    const clientConfiguration = useContext(ClientConfigurationContext);
    const [authToken, setAuthToken] = useState(null);
    const ref = useRef(null);
    const navigate = useNavigate();

    useEffect(() => {
        getVaultApiBearerToken((token) => {
            setAuthToken(token);
        })
    }, []);

    useEffect(() => {
        if (clientConfiguration && clientConfiguration.paymentWidgetUrl && authToken) {
            const iframe = ref.current;

            if (clientConfiguration.environment === "PD1" || clientConfiguration.environment === "PD8" || clientConfiguration.environment === "XP1") {
                iframe.src = clientConfiguration.paymentWidgetUrl + "/pd/vltui/ui/";
            }
            else {
                iframe.src = clientConfiguration.paymentWidgetUrl + "/ui/";
            }

            const props = {
                customerId: isBranch ? branchProfile.agencyId : agencyProfile?.agencyId,
                userId: user?.agentId,
                companyId: "GEICO",
                requestId: getRequestId(),
                authToken: authToken,
                callerApp: 'AgencyIdentity',
                accountUseTypeDefault: 'Checking',
                accountTypeDefault: 'Business',
                companyNamePrefill: isBranch ? branchProfile?.name : agencyProfile?.name,
                needSecondaryButton: true,
                secondaryButtonLabel: 'Cancel',
                primaryButtonLabel: 'Submit',
                checkBoxDefaultSelected: false,
            };

            const handleMessage = (e) => {
                if (e.origin === clientConfiguration.paymentWidgetUrl) {
                    if (e.data.onReady) {
                        const message = {
                            ...props,
                            source: "vault-client"
                        }
                        iframe.contentWindow.postMessage(message, clientConfiguration.paymentWidgetUrl);
                    }
                    else if (e.data.onSuccess) {
                        if (
                            e.data.requestId === props.requestId &&
                            e.data.paymentMethodJSON
                        ) {
                            if (isBranch) {
                                const updatedBranchProfile = {
                                    ...branchProfile,
                                    ivans: branchProfile.ivans ?? {},
                                    sweepDetails: {
                                        ...branchProfile.sweepDetails,
                                        IsSweepsEnrolled: true,
                                        IsPrincipleDeactivated: false,
                                        Last4AccountNumber: e.data.paymentMethodJSON.paymentMethod.last4AccountIdentifier,
                                        AccountNickname: e.data.paymentMethodJSON.nickName,
                                        SweepsPaymentMethodId: e.data.paymentMethodJSON.paymentMethod.paymentMethodId
                                    }
                                };
                                updateSweepDetails(updatedBranchProfile,
                                    () => {
                                        logDetails(`Sucessfully enrolled sweep account for branch: ${branchProfile.agencyId}`);
                                        publishSweepEvent(updatedBranchProfile.agencyId,
                                            "BranchEnrolled",
                                            user?.agentId,
                                            "Sweep event published successfully for agency branch enroll."
                                        );
                                        navigate("/profiles", { state: { agencyProfile: agencyProfile, enrollSuccess: true, showCancelAlert: false } });
                                    });
                            }
                            else {
                                const updatedAgencyProfile = {
                                    ...agencyProfile,
                                    ivans: agencyProfile.ivans ?? {},
                                    sweepDetails: {
                                        ...agencyProfile.sweepDetails,
                                        IsSweepsEnrolled: true,
                                        IsPrincipleDeactivated: false,
                                        Last4AccountNumber: e.data.paymentMethodJSON.paymentMethod.last4AccountIdentifier,
                                        AccountNickname: e.data.paymentMethodJSON.nickName,
                                        SweepsPaymentMethodId: e.data.paymentMethodJSON.paymentMethod.paymentMethodId
                                    }
                                };

                                updateSweepDetails(updatedAgencyProfile,
                                    () => {
                                        logDetails(`Sucessfully enrolled sweep account for agency: ${agencyProfile.agencyId}`);
                                        publishSweepEvent(updatedAgencyProfile.agencyId,
                                            "PrimaryAgencyEnrolled",
                                            user?.agentId,
                                            "Sweep event published successfully for primary agency enroll."
                                        );
                                        navigate("/profiles", { state: { agencyProfile: updatedAgencyProfile, enrollSuccess: true, showCancelAlert: false } });
                                    });
                            }

                        }
                    } else if (e.data.onExit) {
                        onCancel();
                    } else if (e.data.onError) {
                        if (e.data.requestId === props.requestId && e.data.error === 409) {
                            logError(`Bank account details for Request ID '${e.data.requestId}' couldn't be saved as error response is 409.`);
                            setErrorAlert(409);
                        } else if (e.data.requestId === props.requestId && e.data.error === 500) {
                            logError(`Bank account details for Request ID '${e.data.requestId}' couldn't be saved as error response is 500.`);
                            setErrorAlert(500);
                        }
                        else if (e.data.requestId === props.requestId) {
                            logError(`Bank account details for Request ID '${e.data.requestId}' couldn't be saved as error response is ${e.data.error}.`);
                        }
                    }
                }
            };

            window.addEventListener("message", handleMessage);

            return () => {
                window.removeEventListener("message", handleMessage);
            };
        }
    }, [ref, clientConfiguration, user, authToken]);

    return (
        <>
            {clientConfiguration && clientConfiguration.paymentWidgetUrl && authToken && (
                <iframe
                    ref={ref}
                    autoFocus={true}
                    id="vault-widget-iframe"
                    title='Enroll Form Widget'
                    data-testid="vault-widget-iframe"
                    width="100%"
                    height="1300px"
                    frameBorder="0"
                ></iframe>
            )}
        </>
    );
}
